import {
  UserPermission,
  UserPermissions,
  UserRole,
} from "../plugins/Cruxtify/UserPermission";
import {
  DateTimeField,
  NumberField,
  RelationshipField,
  SelectField,
} from "../plugins/Cruxtify/field-types";

import {
  ClientAcceptanceStatus,
  ExpertAcceptanceStatus,
  getAsChoices,
} from "../enums";
import {
  DateTimeProperty,
  NumberProperty,
  RelationshipProperty,
  StringProperty,
} from "../plugins/Cruxtify/property-types";
import { ErCrudModel } from "./ErCrudModel";

import { UserRole as ErUserRole } from "../enums";

export class ExpertMatch extends ErCrudModel {
  protected static _typeLabel = "Expert Match";
  protected static asProperty = "expert_match";
  public static api = {
    path: "expert-matches",
  };
  protected static routeBase = "expert-matches";
  protected static userPermissions = new UserPermissions(
    {
      [UserRole.SuperAdmin]: UserPermission.Edit,
      [UserRole.EntityOwner]: UserPermission.Edit,
    },
    UserPermission.Edit
  );

  protected userIdField = [];

  protected static propertyDefinitions = [
    {
      type: NumberProperty,
      opts: {
        name: "id",
        label: "ID",
        userPermissions: UserPermission.Hidden,
      },
    },
    {
      type: DateTimeProperty,
      opts: {
        name: "created_at",
        label: "Matched At",
        userPermissions: UserPermission.Read,
        computed: true,
      },
    },
    {
      type: DateTimeProperty,
      opts: {
        name: "updated_at",
        label: "Entry Updated At",
        userPermissions: UserPermission.Read,
        computed: true,
      },
    },
    {
      type: DateTimeProperty,
      opts: {
        name: "expert_responded_at",
        label: "Expert Responded At",
        computed: true,
      },
    },
    {
      type: DateTimeProperty,
      opts: {
        name: "client_responded_at",
        label: "Client Responded At",
        computed: true,
      },
    },
    {
      type: DateTimeProperty,
      opts: {
        name: "deleted_at",
        label: "Entry Deleted At",
        userPermissions: UserPermission.Read,
        computed: true,
      },
    },
    {
      type: RelationshipProperty,
      opts: {
        name: "expert",
        label: "Expert",
        relatedModel: "Expert",
        userPermissions: UserPermission.New,
      },
    },
    {
      type: RelationshipProperty,
      opts: {
        name: "submission",
        label: "Case Submission",
        relatedModel: "Submission",
        userPermissions: UserPermission.New,
      },
    },
    {
      type: StringProperty,
      opts: {
        name: "expert_status",
        label: "Expert Response",
        userPermissions: UserPermission.Edit,
      },
    },
    {
      type: StringProperty,
      opts: {
        name: "client_status",
        label: "Client Response",
        userPermissions: UserPermission.Edit,
      },
    },
  ];

  protected static fieldDefinitions = [
    { type: NumberField, opts: { property: "id" } },
    {
      type: DateTimeField,
      opts: { property: "created_at" },
    },
    { type: DateTimeField, opts: { property: "updated_at" } },
    { type: DateTimeField, opts: { property: "deleted_at" } },
    { type: DateTimeField, opts: { property: "expert_responded_at" } },
    { type: DateTimeField, opts: { property: "client_responded_at" } },
    {
      type: SelectField,
      opts: {
        property: "expert_status",
        cols: 4,
        options: getAsChoices(ExpertAcceptanceStatus),
      },
    },

    {
      type: SelectField,
      opts: {
        property: "client_status",
        cols: 4,
        options: getAsChoices(ClientAcceptanceStatus),
        rules: ["required"],
      },
    },

    {
      type: RelationshipField,
      opts: { property: "submission", md: 6 },
    },
    {
      type: RelationshipField,
      opts: { property: "expert", md: 6 },
    },
  ];

  protected getDefaultModelLayoutId(): string {
    return this.$nuxt.$auth.user.is_admin
      ? "admin"
      : this.$nuxt.$auth.user.user_role === ErUserRole.Expert
      ? "expert"
      : "client";
  }

  protected static modelLayoutDefinitions = [
    {
      id: "admin",
      fieldSets: [
        {
          id: "0",
          fields: ["expert", "submission", "client_status", "expert_status"],
        },
        {
          id: "meta",
          fields: ["created_at", "updated_at", "id"],
        },
      ],
    },
    {
      id: "expert",
      fieldSets: [
        {
          id: "0",
          fields: ["client", "submission", "client_status", "expert_status"],
        },
        {
          id: "meta",
          fields: ["created_at", "updated_at", "id"],
        },
      ],
    },
    {
      id: "client",
      fieldSets: [
        {
          id: "0",
          fields: ["expert", "submission", "client_status"],
        },
        {
          id: "meta",
          fields: [
            "expert_responded_at",
            "expert",
            "submission",
            "client_status",
          ],
        },
      ],
    },
  ];

  protected static getDefaultCollectionLayoutId(): string {
    return this.$nuxt.$auth.user.is_admin
      ? "admin"
      : this.$nuxt.$auth.user.user_role === ErUserRole.Expert
      ? "expert"
      : "client";
  }

  protected static collectionLayoutDefinitions = [
    {
      id: "admin",
      useModal: true,
      columnFields: [
        "created_at",
        "expert",
        "submission",
        "expert_status",
        "expert_responded_at",
        "client_status",
        "client_responded_at",
      ],
      enableSearch: true,
      enableDynamicFilters: true,
      disableRowClick: true,
      dynamicFilterOptions: {
        filterFields: [
          "created_at",
          "client_status",
          "expert_status",
          "submission",
          "expert",
          "expert_responded_at",
          "client_responded_at",
          "updated_at",
        ],
      },
      defaultSort: [
        {
          field: "created_at",
          order: "desc",
        },
      ],
    },
    {
      id: "expert",
      // useModal: true,
      // `CollectionLayout.cols` defaults to `"auto"`, which makes the wrapper
      // `<v-col>` CollectionLayout.vue puts around `componentName` a
      // shrink-to-fit flex item -- right for a data table, wrong for a card
      // grid that has to fill the page. Without this the grid is sized by its
      // own content: the seeded long-titled matches overflow it back to full
      // width, but the reporter's single short-titled match collapsed the
      // page to a 58px sliver (asana-1393).
      //
      // This is one half of that fix and does nothing on its own -- measured:
      // the container ABOVE this column shrink-wraps too. See the
      // `.list-expert-match` rule in `assets/base/_cruxtify.scss` for the
      // other half and the full chain.
      cols: 12,
      noRefresh: true,
      noNew: true,
      useDynamicFields: false,
      listTitle: "Case Matches",
      componentName: "expert-match-grid-experts",
      noneText:
        "There are no cases for you to consider at this time. You'll be notified by email when a new case match is available for review.",
      emphasizeNoneText: true,
      defaultSort: [
        {
          field: "created_at",
          order: "asc",
        },
      ],
    },
    {
      id: "client",
      // Same shrink-to-fit wrapper column as the `expert` layout above -- this
      // grid is inside a case page's field set rather than on a list page, so
      // only the `col-auto` half of that defect reaches it, but it reaches it
      // identically: measured at 1440px, one short match rendered a 291px card
      // in an 902px field instead of filling it.
      cols: 12,
      noNew: true,
      noRefresh: true,
      enableSearch: false,
      componentName: "expert-match-grid-client",
      noneText:
        "As experts respond to your submission, they will appear here for your review. You will also be notified by email with their information.",
      emphasizeNoneText: true,
      defaultSort: [
        {
          field: "expert_responded_at",
          order: "asc",
        },
      ],
    },
  ];
}
